import {
  ASSISTANT_TURN_TIMEOUT_MS,
  assistantCardListSchema,
  assistantConversationListSchema,
  assistantConversationSchema,
  assistantPackageSchema,
  assistantTranscribeResponseSchema,
  assistantTurnResponseSchema,
  assistantTurnSchema,
  type AssistantCard,
  type AssistantChatListItem,
  type AssistantCloseReason,
  type AssistantConversation,
  type AssistantInputMode,
  type AssistantPackage,
  type AssistantRatingRequest,
  type AssistantTranscribeRequest,
  type AssistantTranscribeResponse,
  type AssistantTurn,
  type AssistantTurnResponse,
} from "@teamna/shared";
import { keepPreviousData, useMutation, useQuery, useQueryClient, type QueryClient, type UseMutationResult, type UseQueryResult } from "@tanstack/react-query";
import { api } from "../api/client.ts";
import { queryKeys } from "./keys.ts";

/**
 * AI Assistant (strategy repo `teamna/strategy/ai-assistant-v1.md`): the suggestions on Home, the chat
 * list of the signed-in person, one conversation, a new question, Confirm, and the end of a chat.
 * Every write answers with the whole conversation, so the cache takes it as it is. The answer of a
 * question comes back whole (V1 does not stream), so the screen shows "AI Assistant is working"
 * while the mutation runs.
 */

/** GET /v1/owner/businesses/:id/assistant/cards. Null business or AI Assistant off: no request. */
export function useAssistantCards(businessId: string | null): UseQueryResult<AssistantCard[]> {
  return useQuery({
    queryKey: queryKeys.assistantCards(businessId ?? ""),
    queryFn: async () => (await api.get(`/owner/businesses/${businessId}/assistant/cards`, assistantCardListSchema)).cards,
    enabled: businessId !== null,
    staleTime: 60 * 1000,
  });
}

/** GET /v1/owner/businesses/:id/assistant/conversations?q= */
export function useAssistantConversations(businessId: string | null, q = ""): UseQueryResult<AssistantChatListItem[]> {
  const query = q.trim();
  return useQuery({
    queryKey: queryKeys.assistantConversations(businessId ?? "", query),
    queryFn: async () =>
      (await api.get(`/owner/businesses/${businessId}/assistant/conversations`, assistantConversationListSchema, query ? { query: { q: query } } : undefined)).conversations,
    enabled: businessId !== null,
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
  });
}

/** GET /v1/owner/businesses/:id/assistant/conversations/:conversationId */
export function useAssistantConversation(businessId: string | null, conversationId: string | null): UseQueryResult<AssistantConversation> {
  return useQuery({
    queryKey: queryKeys.assistantConversation(businessId ?? "", conversationId ?? ""),
    queryFn: () => api.get(`/owner/businesses/${businessId}/assistant/conversations/${conversationId}`, assistantConversationSchema),
    enabled: businessId !== null && conversationId !== null,
  });
}

/** A conversation came back from a write: put it in the cache, and refresh the lists that show its status. */
function storeConversation(client: QueryClient, businessId: string, conversation: AssistantConversation): void {
  client.setQueryData(queryKeys.assistantConversation(businessId, conversation.id), conversation);
  void client.invalidateQueries({ queryKey: queryKeys.assistantConversationsAll(businessId) });
  void client.invalidateQueries({ queryKey: queryKeys.assistantCards(businessId) });
}

/**
 * A write failed, but the server can have saved it (the question, the change, the end of the chat)
 * before the error: read the chats and the suggestions again, so a retry starts from the truth. The
 * mutation waits for the new data (it stays pending until then), and inactive reads refetch too.
 */
function refreshAfterError(client: QueryClient, businessId: string | null): Promise<void> {
  return businessId ? client.invalidateQueries({ queryKey: queryKeys.assistantAll(businessId), refetchType: "all" }) : Promise.resolve();
}

export type AssistantInput = { conversationId?: string; text: string; inputMode: AssistantInputMode };

/** POST /v1/owner/businesses/:id/assistant/turns */
export function useAssistantTurn(businessId: string | null): UseMutationResult<AssistantTurnResponse, Error, AssistantInput> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: AssistantInput) => 
      api.post(`/owner/businesses/${businessId}/assistant/turns`, input, assistantTurnResponseSchema, { timeoutMs: ASSISTANT_TURN_TIMEOUT_MS }),
    onSuccess: (data) => {
      if (businessId) storeConversation(client, businessId, data.conversation);
    },
    onError: () => refreshAfterError(client, businessId),
  });
}

/**
 * POST /v1/owner/businesses/:id/assistant/transcribe: a recorded question becomes text for the
 * compose box. Nothing is saved, so nothing in the cache changes.
 */
export function useAssistantTranscribe(businessId: string | null): UseMutationResult<AssistantTranscribeResponse, Error, AssistantTranscribeRequest> {
  return useMutation({
    mutationFn: (input: AssistantTranscribeRequest) => api.post(`/owner/businesses/${businessId}/assistant/transcribe`, input, assistantTranscribeResponseSchema),
  });
}

export type AssistantEntry = "home_sheet" | "assistant_page";

/** POST /v1/owner/businesses/:id/assistant/cards/:cardId/open: the chat of a suggestion, made on the first open. */
export function useOpenAssistantCard(businessId: string | null): UseMutationResult<AssistantConversation, Error, { cardId: string; entry: AssistantEntry }> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ cardId, entry }) => api.post(`/owner/businesses/${businessId}/assistant/cards/${cardId}/open`, { entry }, assistantConversationSchema),
    onSuccess: (conversation) => {
      if (businessId) storeConversation(client, businessId, conversation);
    },
    // A suggestion that is not true any more is gone: Home lists the rest.
    onError: () => {
      if (businessId) void client.invalidateQueries({ queryKey: queryKeys.assistantCards(businessId) });
    },
  });
}

/** POST /v1/owner/businesses/:id/assistant/turns/:turnId/more: one level deeper, with no model call. */
export function useAssistantMore(businessId: string | null): UseMutationResult<AssistantConversation, Error, { turnId: string; block: number }> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ turnId, block }) => api.post(`/owner/businesses/${businessId}/assistant/turns/${turnId}/more`, { block }, assistantConversationSchema),
    onSuccess: (conversation) => {
      if (businessId) storeConversation(client, businessId, conversation);
    },
    onError: () => refreshAfterError(client, businessId),
  });
}

/** POST /v1/owner/businesses/:id/assistant/actions/:actionId/confirm */
export function useConfirmAssistantAction(businessId: string | null): UseMutationResult<AssistantConversation, Error, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (actionId: string) => api.post(`/owner/businesses/${businessId}/assistant/actions/${actionId}/confirm`, {}, assistantConversationSchema),
    onSuccess: (conversation) => {
      if (businessId) storeConversation(client, businessId, conversation);
    },
    onError: () => refreshAfterError(client, businessId),
  });
}

export type AssistantChatEnd = { conversationId: string } & ({ kind: "done" } | { kind: "not-now" } | { kind: "close"; reason: AssistantCloseReason });

/** POST .../conversations/:id/done, /not-now or /close */
export function useEndAssistantChat(businessId: string | null): UseMutationResult<AssistantConversation, Error, AssistantChatEnd> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (end: AssistantChatEnd) =>
      api.post(
        `/owner/businesses/${businessId}/assistant/conversations/${end.conversationId}/${end.kind}`,
        end.kind === "close" ? { reason: end.reason } : {},
        assistantConversationSchema,
      ),
    onSuccess: (conversation) => {
      if (businessId) storeConversation(client, businessId, conversation);
    },
    onError: () => refreshAfterError(client, businessId),
  });
}

/** DELETE /v1/owner/businesses/:id/assistant/conversations/:conversationId */
export function useArchiveAssistantConversation(businessId: string | null): UseMutationResult<void, Error, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: string) => api.delete(`/owner/businesses/${businessId}/assistant/conversations/${conversationId}`),
    onSuccess: (_data, conversationId) => {
      if (!businessId) return;
      client.removeQueries({ queryKey: queryKeys.assistantConversation(businessId, conversationId) });
      void client.invalidateQueries({ queryKey: queryKeys.assistantConversationsAll(businessId) });
    },
    onError: () => refreshAfterError(client, businessId),
  });
}

export type AssistantRatingInput = { conversationId: string; turnId: string } & AssistantRatingRequest;

/**
 * PUT .../assistant/turns/:turnId/rating: thumbs up or down on one answer, or `rating: null` to take
 * it back. The answer is the turn; the cached conversation takes it in place.
 */
export function useRateAssistantTurn(businessId: string | null): UseMutationResult<AssistantTurn, Error, AssistantRatingInput> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ turnId, rating, reason, note }) =>
      api.put(`/owner/businesses/${businessId}/assistant/turns/${turnId}/rating`, { rating, reason, note }, assistantTurnSchema),
    onSuccess: (turn, input) => {
      if (!businessId) return;
      client.setQueryData<AssistantConversation>(queryKeys.assistantConversation(businessId, input.conversationId), (conversation) =>
        conversation ? { ...conversation, turns: conversation.turns.map((row) => (row.id === turn.id ? turn : row)) } : conversation,
      );
    },
  });
}

/**
 * POST (add, or keep after a remove) or DELETE (remove) .../billing/ai-assistant: the AI Assistant
 * package. The business list carries the package, so it refreshes, and the Billing page too (its
 * next amount changes).
 */
export function useAssistantPackage(businessId: string | null): UseMutationResult<AssistantPackage, Error, "add" | "remove"> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (change) => {
      const path = `/owner/businesses/${businessId}/billing/ai-assistant`;
      return change === "add" ? api.post(path, {}, assistantPackageSchema) : api.remove(path, assistantPackageSchema);
    },
    // Also after an error: the change can be saved even when the answer did not arrive. The mutation
    // stays pending until the new state is in, so the buttons never offer a change already made.
    onSettled: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.businesses }),
        businessId ? client.invalidateQueries({ queryKey: queryKeys.owner.billing(businessId) }) : null,
      ]),
  });
}
